// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ChargeStatusSummary,
  CurrencyChargeStatusSummary,
} from '@/billing/api/types';
import { CARD_COLOR_TOKENS } from '@/billing/components/charge-status-constants';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import { ChargeStatus } from '@/constants/charge-status';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { pluralize } from '@/utilities/pluralize';
import { Box, Grid } from '@mui/material';

interface ChargeSummaryCardsProps {
  // `null` signals "data still loading". Each tile renders a skeleton in
  // that state rather than fabricating zero values.
  summary: ChargeStatusSummary | null;
}

// Fixed tile order so the skeleton layout matches the loaded layout; this
// prevents visual reshuffle when data arrives.
const CHARGE_TILES: {
  status: ChargeStatus;
  label: string;
  amount: (bucket: CurrencyChargeStatusSummary) => string;
  count: (bucket: CurrencyChargeStatusSummary) => number;
}[] = [
  {
    status: 'PLANNED',
    label: 'Planned',
    amount: (bucket) => bucket.plannedAmount,
    count: (bucket) => bucket.plannedCount,
  },
  {
    status: 'BILLABLE',
    label: 'Billable',
    amount: (bucket) => bucket.billableAmount,
    count: (bucket) => bucket.billableCount,
  },
  {
    status: 'INVOICED',
    label: 'On invoice',
    amount: (bucket) => bucket.invoicedAmount,
    count: (bucket) => bucket.invoicedCount,
  },
  {
    status: 'CANCELLED',
    label: 'Cancelled',
    amount: (bucket) => bucket.cancelledAmount,
    count: (bucket) => bucket.cancelledCount,
  },
];

// Charge status totals, bucketed per total-price currency; single-currency
// tenants (the common case) see exactly one group.
export function ChargeSummaryCards(props: ChargeSummaryCardsProps) {
  const { summary } = props;

  if (summary === null) {
    return (
      <Box sx={{ mb: 2 }}>
        <CurrencyTileRow bucket={null} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <PerCurrencySections
        buckets={summary.byCurrency}
        renderBucket={(bucket) => <CurrencyTileRow bucket={bucket} />}
      />
    </Box>
  );
}

interface CurrencyTileRowProps {
  // `null` signals the loading state; every tile renders a skeleton.
  bucket: CurrencyChargeStatusSummary | null;
}

function CurrencyTileRow(props: CurrencyTileRowProps) {
  const { bucket } = props;
  const isLoading = bucket === null;

  return (
    <Grid container spacing={3}>
      {CHARGE_TILES.map((tile) => (
        <Grid item xs={12} sm={6} md={3} key={tile.status}>
          <SummaryCard>
            <PrimaryMetric
              label={tile.label}
              value={
                bucket
                  ? formatAmountWithCurrency(
                      tile.amount(bucket),
                      bucket.isoCode
                    )
                  : ''
              }
              color={CARD_COLOR_TOKENS[tile.status]}
              isLoading={isLoading}
              caption={
                bucket
                  ? pluralize(tile.count(bucket), { singular: 'charge' })
                  : undefined
              }
            />
          </SummaryCard>
        </Grid>
      ))}
    </Grid>
  );
}
